import React from 'react';
import { Controller } from 'react-hook-form';
import DOMPurify from 'dompurify';
import { FieldTypeMap } from 'components/form-fields-v2';
import { shouldShowPremiumWarning } from 'helpers';
import styles from '../styles/PolicyDetailsForm.module.scss';
import { getValueByType } from '../utils';
import { HelpTooltip } from 'components/tooltip';

const FormField = (props) => {
  const {
    fieldKey: key,
    disabled,
    confirmation,
    control,
    error,
    formValues,
    hideDisabled,
    isOwner,
    label,
    required,
    type,
    validator,
    value,
    onConfirm,
    tooltip,
  } = props;

  if (disabled) {
    const valueByType = getValueByType(props, value);

    if (hideDisabled) return null;

    const showConfirm =
      isOwner && confirmation && shouldShowPremiumWarning(formValues);

    return (
      <div
        className={classnames(
          styles.disabledField,
          required && !formValues.employerId && !value && styles.errorField,
        )}
      >
        <div className={styles.labelWrap}>
          <label
            className={styles.label}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(label) }}
          />
          {tooltip && (
            <HelpTooltip
              toggleContainerClassName={styles.toggle}
              content={tooltip}
            />
          )}
        </div>
        {Array.isArray(valueByType) ? (
          valueByType.map((v) => (
            <h3 key={v} className={styles.value}>
              {v}
            </h3>
          ))
        ) : (
          <h3 className={styles.value}>{valueByType}</h3>
        )}
        {showConfirm && (
          <div className={styles.confirmation} onClick={onConfirm}>
            {confirmation}
          </div>
        )}
      </div>
    );
  }

  const FieldComponent = FieldTypeMap[type];

  return (
    <Controller
      key={key}
      control={control}
      name={key}
      rules={{
        required,
        validate: validator,
      }}
      render={({ field }) => {
        return (
          <FieldComponent
            {...field}
            {...props}
            error={error?.message || !!error}
            errorText={error?.message}
            className={styles.field}
            labelClassName={styles.label}
            inputClassName={styles.input}
          />
        );
      }}
    />
  );
};

const FormFields = ({
  control,
  disabled,
  errors,
  fields,
  loading,
  values,
  ...otherProps
}) => {
  return (
    <div className={styles.fields}>
      {fields.map((field, idx) => {
        if (Array.isArray(field)) {
          return (
            <div
              key={`field_${idx}`}
              className={classnames(
                styles.fieldPair,
                disabled && styles.disabled,
              )}
            >
              {field.map((f) => {
                return (
                  <FormField
                    {...f}
                    key={f.key}
                    fieldKey={f.key}
                    control={control}
                    disabled={disabled}
                    error={errors[f.key]}
                    value={values[f.key]}
                    formValues={values}
                    {...otherProps}
                  />
                );
              })}
            </div>
          );
        }

        return (
          <FormField
            {...field}
            key={field.key}
            fieldKey={field.key}
            control={control}
            disabled={disabled}
            error={errors[field.key]}
            loading={loading[field.key]}
            value={values[field.key]}
            defaultValue={field.defaultValue}
            formValues={values}
            {...otherProps}
          />
        );
      })}
    </div>
  );
};

export default FormFields;
